import type { Campaign, ICampaignAdvanceSetting } from '~/modules/campaign/types';
import type { CampaignPriority, CampaignType } from '~/types/graphql';
import type { ICampaignGroup } from '~/modules/campaign-group';

import {
  CAMPAIGN_STATUS,
  DEFAULT_SETTING_TARGET,
  CAMPAIGN_ENDED_STATUS,
} from '~/constants/campaign';
import { DESTINATION_PREVIEW_SUFFIX } from '../constants';
import { findVariantByOrigin } from './campaign-variant';
import { deepCopy, parseTemplateInfoBy } from '~/utils';
import { formatDateBy } from '~/utils/dateTime';

const getTemplateSuffix = (templateSuffix?: string) => {
  return templateSuffix ? parseTemplateInfoBy({ templateSuffix })?.suffix : '';
};

const createParamView = (
  templateSuffixA: string,
  templateSuffixB: string,
  active?: 'variantA' | 'variantB',
) => {
  return active === 'variantA'
    ? { view: templateSuffixA }
    : active === 'variantB'
      ? { view: templateSuffixB }
      : {};
};

const createMapParamByCampaign = ({
  templateSuffixA = '',
  templateSuffixB = '',
  redirectUrlA = '',
  redirectUrlB = '',
  options,
}: {
  templateSuffixA?: string;
  templateSuffixB?: string;
  redirectUrlA?: string;
  redirectUrlB?: string;
  options?: { params?: { view?: string }; active?: 'variantA' | 'variantB' };
}) => {
  return {
    _ab: '0',
    key: new Date().getTime().toString(),
    gemPreview: 'true',
    ...(options?.params ?? {}),
    templateSuffixA,
    templateSuffixB,
    redirectUrlA,
    redirectUrlB,
    ...createParamView(templateSuffixA, templateSuffixB, options?.active),
  };
};

export const getSearchParamByCampaign = (
  campaign: Campaign,
  options?: { params?: { view?: string }; active?: 'variantA' | 'variantB' },
): string => {
  const originVariant = findVariantByOrigin(campaign, true);
  const nonOriginVariant = findVariantByOrigin(campaign, false);

  const templateSuffixA = originVariant ? getTemplateSuffix(originVariant.templateSuffix) : '';
  const templateSuffixB = nonOriginVariant
    ? getTemplateSuffix(nonOriginVariant.templateSuffix)
    : '';
  const redirectUrlA = originVariant?.template?.redirectURL ?? '';
  const redirectUrlB = nonOriginVariant?.template?.redirectURL ?? '';

  const mapParamByCampaign = createMapParamByCampaign({
    templateSuffixA,
    templateSuffixB,
    redirectUrlA,
    redirectUrlB,
    options,
  });

  return new URLSearchParams(mapParamByCampaign).toString();
};

export interface IGetSearchParamByDestinationCampaignParams {
  controlURL: string;
  variantURL: string;
  campaignType: CampaignType;
}

/**
 * Price and URL campaigns have no template suffix — Control and Variant are two real storefront
 * pages. Reuses the same gx-preview.js toggle as `getSearchParamByCampaign`, with placeholder
 * suffixes (see `DESTINATION_PREVIEW_SUFFIX`) standing in for the template suffixes it expects.
 *
 * No `view` param on purpose: Shopify reads it to pick an alternate template, so a placeholder
 * there resolves to a non-existent `<type>.<placeholder>` and falls back to the default template
 * instead of the one assigned to the page. gx-preview.js defaults its active side to
 * `templateSuffixA` (Control) when `view` is absent, which is what this preview always opens on.
 */
export const getSearchParamByDestinationCampaign = ({
  controlURL,
  variantURL,
  campaignType,
}: IGetSearchParamByDestinationCampaignParams): string => {
  const mapParamByCampaign = createMapParamByCampaign({
    templateSuffixA: DESTINATION_PREVIEW_SUFFIX.CONTROL,
    templateSuffixB: DESTINATION_PREVIEW_SUFFIX.VARIANT,
    redirectUrlA: controlURL,
    redirectUrlB: variantURL,
  });

  // Key name is matched by string literal on the theme-extension side (PARAM_CAMPAIGN_TYPE), same
  // convention as templateSuffixA/redirectUrlA above — the two packages share no common types.
  return new URLSearchParams({ ...mapParamByCampaign, campaignType }).toString();
};

export const sortCampaignByStartTime = (a: Campaign, b: Campaign): number => {
  if (a.startedAt && b.startedAt) {
    return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
  }
  if (a.startedAt && !b.startedAt) {
    return -1;
  }
  if (!a.startedAt && b.startedAt) {
    return 1;
  }

  if (!a.updatedAt || !b.updatedAt) return 0;
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
};

export const generateNameWithTime = (name: string, format = 'MMM D, HH:mm:ss') =>
  `${name} - ${formatDateBy(format)}`;

export const campaignAllowedSyncWithLivePages = (campaign: Campaign | ICampaignGroup) => {
  return campaign?.status === CAMPAIGN_STATUS.RUNNING;
};

/**
 * Build updated conditions with targetMarkets.
 * `targetMarkets` is implicitly "ALL" on create but not persisted.
 * Only update when it already exists or differs from the default.
 */
export const buildConditionsWithTargetMarkets = <T extends { targetMarkets?: string[] }>(
  currentConditions: T | undefined,
  newTargetMarkets: string[] | undefined,
): T | undefined => {
  if (!newTargetMarkets?.length) {
    return currentConditions;
  }

  const hasExistingTargetMarkets = currentConditions?.targetMarkets !== undefined;
  const isDefaultValue =
    newTargetMarkets.length === 1 && newTargetMarkets[0] === DEFAULT_SETTING_TARGET[0];

  if (hasExistingTargetMarkets || !isDefaultValue) {
    return {
      ...currentConditions,
      targetMarkets: newTargetMarkets,
    } as T;
  }

  return currentConditions;
};

/**
 * Build an updated campaign draft from advance-setting form values: priority, targeting, conditions,
 * and the traffic split (origin variant gets `trafficRate`, the other variant gets the complement).
 * Shared by campaign-template and campaign-price so their advance-setting hooks stay in sync.
 */
export const buildCampaignAdvanceSettingUpdate = (
  campaign: Campaign,
  settings: ICampaignAdvanceSetting,
): Campaign => {
  const originTrafficRate = +settings.trafficRate;
  let variantTrafficRate = 1 - originTrafficRate;
  if (variantTrafficRate < 1) variantTrafficRate = +variantTrafficRate.toFixed(2);

  const updatedConditions = buildConditionsWithTargetMarkets(
    campaign.conditions,
    settings.targetMarkets,
  );

  return {
    ...campaign,
    priority: settings.priority as CampaignPriority,
    targetDevices: settings.targetDevices,
    targetVisitorTypes: settings.targetVisitorTypes,
    targetChannels: settings.targetChannels,
    variants: campaign.variants?.map((variant) => ({
      ...variant,
      id: variant?.id ?? '',
      trafficRate: variant?.isOrigin ? originTrafficRate : variantTrafficRate,
    })),
    ...(updatedConditions ? { conditions: updatedConditions } : {}),
  };
};

export const getActiveCampaigns = (campaigns: Campaign[] | undefined) => {
  if (!campaigns) return [];

  return deepCopy(campaigns).filter(
    (c) => !c.deletedAt && c.status && !CAMPAIGN_ENDED_STATUS.includes(c.status),
  );
};
